import { CalendarIcon } from '@heroicons/react/24/outline';
import { Avatar, Card, CardBody, Divider, ScrollShadow, Spinner, Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@nextui-org/react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { patchFetcher } from '../../../../services/api';
import toast from 'react-hot-toast';
import { diffForHuman, formattedDate, hasValidRole } from '../../../../services/helpers';
import StatusTicket from '../../../../enums/statusTicket';
import ControlledInput from '../../../elements/ControlledInput';
import ControlledReactSelect from '../../../elements/ControlledReactSelect';
import useAuth from '../../../../hooks/useAuth';
import RolesAccess from '../../../../enums/roles';

export default function Timeline({ data, isLoading, className, mutate }) {
  const {user, roles} = useAuth(); 
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const statusHistory = data?.statusHistory?.map((item, index, array) => {
    if (index === 0) {
      return { ...item, timeDiff: null };
    } else {
      const previousDate = array[index - 1].changedAt;
      return { ...item, timeDiff: diffForHuman(previousDate, item.changedAt) };
    }
  });
  

  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

    const statusOptions = Object.entries(StatusTicket).map(([key, value]) => ({
    label: value,
    value: value,
    }));

  const onSubmitForm = async (formData) => {
    try {
      
      const dataSend = {
        status: formData.status,
        changeAt: formData.changeAt,
        notes: formData.notes,
        changedBy: user.id,
      }

      await patchFetcher(`/api/tickets`, data.id, dataSend);
      mutate();
      reset();
      toast.success('Status updated successfully!');
      toggleModal();
    } catch (error) {
      console.log(error);
      toast.error('Failed to update status.');
    }
  };

  return (
    <Card radius="sm" className={className}>
      <CardBody>
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-lg font-bold">Timeline</h4>
          {
            hasValidRole(roles, [RolesAccess.PIC_KONSULTASI, RolesAccess.SUPER_ADMIN]) && (
              <Button color="primary" size="sm" onPress={toggleModal}>
              Update Status
            </Button>
            )
          }

        </div>
        <Divider className="my-3" />
        <ScrollShadow className="p-5 h-[400px]">
          {isLoading && (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          )}
          <ol className="relative border-s border-line">
            {statusHistory?.map((item, index) => (
              <li key={index} className="mb-10 ms-6">
                <span className="absolute flex items-center justify-center bg-blue-100 rounded-full -start-4 ring-8 ring-white">
                  <Avatar icon={<CalendarIcon className="size-4" />} size="sm" />
                </span>
                <div className="p-4 bg-white border border-line rounded-lg shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-normal">{item.newStatus}</span>
                    <div className="flex flex-col">
                      <span className="text-xs font-normal text-ink-400">Changed At</span>
                      <time className="text-xs font-normal">
                        {formattedDate(item.changedAt)} {item.timeDiff && `(${item.timeDiff})`}
                      </time>
                    </div>
                  </div>
                  <div className="p-3 text-xs italic font-normal text-ink-500 border border-line rounded-lg bg-surface-2">
                    <span>Notes: </span>
                    {item.notes || '-'}
                  </div>
                  <span className="text-xs leading-3 text-ink-500">Changed by: {item.changedBy}</span>
                </div>
              </li>
            ))}
          </ol>
        </ScrollShadow>

        {/* Modal for Updating Status */}
        <Modal isOpen={isModalOpen} onClose={toggleModal} size="md" isDismissable={!isSubmitting}>
          <ModalContent>
            <ModalHeader>Update Ticket Status</ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit(onSubmitForm)}>
                <div className="flex flex-col gap-3">
                  <ControlledInput
                    name="changeAt"
                    label="Change Date"
                    type="date"
                    isRequired
                    control={control}
                    rules={{ required: 'Change date is required' }}
                  />
                  <ControlledReactSelect
                    name="status"
                    label="New Status"
                    options={statusOptions}
                    isRequired
                    control={control}
                    rules={{ required: 'New status is required' }}
                  />
                  <ControlledInput
                    name="notes"
                    label="Notes"
                    type="text"
                    isRequired
                    control={control}
                    rules={{ required: 'Notes are required' }}
                  />
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button color="primary" type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
                    Submit
                  </Button>
                  <Button color="danger" variant="faded" onPress={toggleModal} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </div>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
}
